/**
 * <s-purchase-options> — Subscribe & save card inside the product form.
 *
 * Required child markup:
 *   details — open = subscription, closed = one-time purchase.
 *   select[data-plan-select] — delivery frequency; option values are selling plan ids.
 * Form coupling:
 *   input[name="selling_plan"] — hidden form field read by s-product-form; disabled for one-time.
 * Attributes:
 *   data-preview — editor-only preview with sample frequencies; never touches the form or URL.
 *   data-required — the product can only be bought on a plan, so the card stays open.
 * Partial contract:
 *   "product-form" and "product-title" — must match the {% partial %} names in templates/product.liquid.
 *   ?selling_plan= is the Shopify URL param that makes Liquid render the selected plan's prices.
 */
import { partials } from '@shopify/partial-rendering';
import { transition } from '@theme/view-transition';
import { announceGlobalLiveRegion } from '@theme/live-region';

class SPurchaseOptions extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const details = this.querySelector('details');

    details?.addEventListener('toggle', () => {
      if (this.hasAttribute('data-preview')) return;
      if (!details.open && this.hasAttribute('data-required')) {
        details.open = true;
        return;
      }
      const planInput = this.querySelector('input[name="selling_plan"]');
      const current = planInput instanceof HTMLInputElement && !planInput.disabled ? planInput.value : '';
      const next = details.open ? this.#selectedPlan() : '';
      if (next !== current) this.#update(next);
    }, { signal });

    this.addEventListener('change', (event) => {
      const select = event.target;
      if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-plan-select')) return;
      if (this.hasAttribute('data-preview')) return;
      this.#update(select.value);
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #selectedPlan() {
    const select = this.querySelector('select[data-plan-select]');
    return select instanceof HTMLSelectElement ? select.value : '';
  }

  async #update(planId) {
    const host = this.closest('[data-product-form]');
    const updatedMessage = this.dataset.updatedMessage;
    const errorMessage = this.dataset.errorMessage;

    // Sync the submitted field first so Add to cart is correct even before prices refresh.
    const planInput = host?.querySelector('input[name="selling_plan"]');
    if (planInput instanceof HTMLInputElement) {
      planInput.value = planId;
      planInput.disabled = !planId;
    }

    const url = new URL(location.href);
    if (planId) {
      url.searchParams.set('selling_plan', planId);
    } else {
      url.searchParams.delete('selling_plan');
    }
    history.replaceState(history.state, '', url);

    // A newer choice supersedes an older in-flight refresh.
    host?.sPurchaseOptionsController?.abort();
    const controller = new AbortController();
    if (host) host.sPurchaseOptionsController = controller;
    this.setAttribute('aria-busy', 'true');

    try {
      const html = await partials.fetch('product-form', 'product-title', { url: url.toString(), signal: controller.signal });
      if (controller.signal.aborted) return;

      const hadFocus = this.contains(document.activeElement);
      await transition(() => partials.apply(html));

      // The refreshed card must match the buyer's choice: stay open (with the chosen plan) for
      // subscription, closed for one-time, even if the server render came back in another state.
      // Sync the select and hidden input before toggling so the new card's toggle handler sees no change.
      const freshCard = host?.querySelector('s-purchase-options');
      const freshDetails = freshCard?.querySelector('details');
      const freshSelect = freshCard?.querySelector('select[data-plan-select]');
      const freshInput = host?.querySelector('input[name="selling_plan"]');
      if (planId && freshSelect instanceof HTMLSelectElement && [...freshSelect.options].some((option) => option.value === planId)) {
        freshSelect.value = planId;
      }
      if (freshInput instanceof HTMLInputElement) {
        freshInput.value = planId;
        freshInput.disabled = !planId;
      }
      if (freshDetails instanceof HTMLDetailsElement && freshDetails.open !== Boolean(planId)) {
        freshDetails.open = Boolean(planId);
      }

      if (hadFocus && host) {
        const target = planId
          ? host.querySelector('select[data-plan-select]')
          : host.querySelector('s-purchase-options summary');
        if (target instanceof HTMLElement) target.focus();
      }
      announceGlobalLiveRegion(updatedMessage);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      announceGlobalLiveRegion(errorMessage);
      console.error('[s-purchase-options] Partial fetch failed:', error);
    } finally {
      if (this.isConnected) this.removeAttribute('aria-busy');
      if (host?.sPurchaseOptionsController === controller) delete host.sPurchaseOptionsController;
    }
  }
}

if (!customElements.get('s-purchase-options')) {
  customElements.define('s-purchase-options', SPurchaseOptions);
}
